import { useState, useEffect } from 'react'
import { Outlet, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useAuth from '../hooks/useAuth'
import { db, isAbortError } from '../lib/supabase'
import LanguageSwitcher from './LanguageSwitcher'

export default function AdminLayout() {
  const { t } = useTranslation()
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [unreadContactCount, setUnreadContactCount] = useState(0)
  const [pendingFacilitatorCount, setPendingFacilitatorCount] = useState(0)

  useEffect(() => {
    db.getUnreadContactCount()
      .then(count => setUnreadContactCount(count || 0))
      .catch(err => { if (!isAbortError(err)) console.error(err) })
    db.getPendingFacilitatorCount()
      .then(count => setPendingFacilitatorCount(count || 0))
      .catch(err => { if (!isAbortError(err)) console.error(err) })
  }, [])

  const handleSignOut = async () => {
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <div className="min-h-screen">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center text-white font-display font-semibold text-sm shadow">
              CSI
            </div>
            <h1 className="font-display text-xl font-medium text-gray-800">
              {t('nav.adminDashboard')}
            </h1>
          </Link>

          <nav className="flex items-center gap-2">
            <Link
              to="/admin"
              className="px-3 py-1.5 text-sm font-semibold text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {t('nav.submissions')}
            </Link>
            <Link
              to="/admin/facilitators"
              className="relative px-3 py-1.5 text-sm font-semibold text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {t('nav.facilitators')}
              {pendingFacilitatorCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-amber-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center px-1">
                  {pendingFacilitatorCount}
                </span>
              )}
            </Link>
            <Link
              to="/admin/contact"
              className="relative px-3 py-1.5 text-sm font-semibold text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {t('nav.contact')}
              {unreadContactCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center px-1">
                  {unreadContactCount}
                </span>
              )}
            </Link>
          </nav>

          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <span className="text-sm text-gray-500 hidden sm:inline">
              {user?.email}
            </span>
            <Link
              to="/admin/change-password"
              className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 rounded-lg transition-all hover:bg-gray-200"
            >
              {t('nav.changePassword')}
            </Link>
            <button
              onClick={handleSignOut}
              className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 rounded-lg transition-all hover:bg-gray-200"
            >
              {t('nav.signOut')}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 py-6">
        <Outlet />
      </main>
    </div>
  )
}
